import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ErrorType } from '../../api/errorHandling';
import {
 AccessGroupsDTO,
 ConnectionKeysExtendedDTO,
 CreateUpdateUsersDTO,
 FilterDTO,
 GroupDTO,
 SetConnectionKeysDTO,
 StructureType,
 UsersDTO,
} from './usersTypes';

import { commonReducers, CommonState } from '../_common/commonReducers';

interface UsersState extends CommonState {
 user?: UsersDTO | null;
 groups?: GroupDTO[];
 group?: GroupDTO | null;
 accessGroups?: AccessGroupsDTO[];
 databases?: ConnectionKeysExtendedDTO[];
 apiKey?: string | null;
 error: { [key: string]: ErrorType | null };
}

export const initialState: UsersState = {
 user: null,
 groups: undefined,
 group: null,
 accessGroups: undefined,
 databases: undefined,
 apiKey: null,
 loading: {},
 success: {},
 error: {},
 items: [],
 aggregate: null,
 item: null,
 structure: [],
 documents: [],
 rowCount: '0',
 excel: null,
};

const usersSlice = createSlice({
 name: 'users',
 initialState,
 reducers: {
  ...commonReducers,
  usersGetSelfStart(state, action: PayloadAction<{ fields?: string }>) {
   return {
    ...state,
    loading: { ...state.loading, usersGetSelf: true },
   };
  },
  usersGetSelfSuccess(state, action: PayloadAction<UsersDTO>) {
   return {
    ...state,
    user: action.payload,
    success: { ...state.success, usersGetSelf: true },
    loading: { ...state.loading, usersGetSelf: null },
   };
  },
  usersGetSelfFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, usersGetSelf: null },
    loading: { ...state.loading, usersGetSelf: null },
   };
  },
  usersCreateUserStart(state, action: PayloadAction<CreateUpdateUsersDTO>) {
   return {
    ...state,
    loading: { ...state.loading, usersCreateUser: true },
   };
  },
  usersCreateUserSuccess(state, action: PayloadAction<UsersDTO>) {
   return {
    ...state,
    user: action.payload,
    success: { ...state.success, usersCreateUser: true },
    loading: { ...state.loading, usersCreateUser: null },
   };
  },
  usersCreateUserFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, usersCreateUser: null },
    loading: { ...state.loading, usersCreateUser: null },
   };
  },
  usersUpdateSelfStart(state, action: PayloadAction<CreateUpdateUsersDTO>) {
   return {
    ...state,
    loading: { ...state.loading, usersUpdateSelf: true },
   };
  },
  usersUpdateSelfSuccess(state, action: PayloadAction<UsersDTO>) {
   return {
    ...state,
    user: action.payload,
    success: { ...state.success, usersUpdateSelf: true },
    loading: { ...state.loading, usersUpdateSelf: null },
   };
  },
  usersUpdateSelfFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, usersUpdateSelf: null },
    loading: { ...state.loading, usersUpdateSelf: null },
   };
  },
  usersUpdateSelfFinish(state) {
   return {
    ...state,
    success: { ...state.success, usersUpdateSelf: null },
    loading: { ...state.loading, usersUpdateSelf: null },
   };
  },
  usersGetUserGroupsStart(state, action: PayloadAction<FilterDTO>) {
   return {
    ...state,
    loading: { ...state.loading, usersGetUserGroups: true },
   };
  },
  usersGetUserGroupsSuccess(state, action: PayloadAction<GroupDTO[]>) {
   return {
    ...state,
    groups: action.payload,
    success: { ...state.success, usersGetUserGroups: true },
    loading: { ...state.loading, usersGetUserGroups: null },
   };
  },
  usersGetUserGroupsFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, usersGetUserGroups: null },
    loading: { ...state.loading, usersGetUserGroups: null },
   };
  },
  usersGetUserGroupStart(
   state,
   action: PayloadAction<{ id: number; fields?: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, usersGetUserGroup: true },
   };
  },
  usersGetUserGroupSuccess(state, action: PayloadAction<GroupDTO>) {
   return {
    ...state,
    group: action.payload,
    success: { ...state.success, usersGetUserGroup: true },
    loading: { ...state.loading, usersGetUserGroup: null },
   };
  },
  usersGetUserGroupFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, usersGetUserGroup: null },
    loading: { ...state.loading, usersGetUserGroup: null },
   };
  },
  usersCreateUserGroupStart(state, action: PayloadAction<any>) {
   return {
    ...state,
    loading: { ...state.loading, usersCreateUserGroup: true },
   };
  },
  usersCreateUserGroupSuccess(state, action: PayloadAction<GroupDTO>) {
   return {
    ...state,
    group: action.payload,
    success: { ...state.success, usersCreateUserGroup: true },
    loading: { ...state.loading, usersCreateUserGroup: null },
   };
  },
  usersCreateUserGroupFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, usersCreateUserGroup: null },
    loading: { ...state.loading, usersCreateUserGroup: null },
   };
  },
  usersUpdateUserGroupStart(
   state,
   action: PayloadAction<{ id: number; data: any }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, usersUpdateUserGroup: true },
   };
  },
  usersUpdateUserGroupSuccess(state, action: PayloadAction<GroupDTO>) {
   return {
    ...state,
    group: action.payload,
    success: { ...state.success, usersUpdateUserGroup: true },
    loading: { ...state.loading, usersUpdateUserGroup: null },
   };
  },
  usersUpdateUserGroupFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, usersUpdateUserGroup: null },
    loading: { ...state.loading, usersUpdateUserGroup: null },
   };
  },
  usersGetStructureStart(state, action: PayloadAction<{ lang?: string }>) {
   return {
    ...state,
    loading: { ...state.loading, usersGetStructure: true },
   };
  },
  usersGetStructureSuccess(state, action: PayloadAction<StructureType[]>) {
   return {
    ...state,
    structure: action.payload,
    success: { ...state.success, usersGetStructure: true },
    loading: { ...state.loading, usersGetStructure: null },
   };
  },
  usersGetStructureFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, usersGetStructure: null },
    loading: { ...state.loading, usersGetStructure: null },
   };
  },
  usersGetUserAccessGroupsStart(
   state,
   action: PayloadAction<{ userId: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, usersGetUserAccessGroups: true },
   };
  },
  usersGetUserAccessGroupsSuccess(
   state,
   action: PayloadAction<AccessGroupsDTO[]>,
  ) {
   return {
    ...state,
    accessGroups: action.payload,
    success: { ...state.success, usersGetUserAccessGroups: true },
    loading: { ...state.loading, usersGetUserAccessGroups: null },
   };
  },
  usersGetUserAccessGroupsFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, usersGetUserAccessGroups: null },
    loading: { ...state.loading, usersGetUserAccessGroups: null },
   };
  },
  usersGetSelfAccessGroupStart(
   state,
   action: PayloadAction<{ accessgroupId: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, usersGetSelfAccessGroup: true },
   };
  },
  usersGetSelfAccessGroupSuccess(
   state,
   action: PayloadAction<AccessGroupsDTO[]>,
  ) {
   return {
    ...state,
    accessGroups: action.payload,
    success: { ...state.success, usersGetSelfAccessGroup: true },
    loading: { ...state.loading, usersGetSelfAccessGroup: null },
   };
  },
  usersGetSelfAccessGroupFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, usersGetSelfAccessGroup: null },
    loading: { ...state.loading, usersGetSelfAccessGroup: null },
   };
  },
  usersGetSelfAccessGroupsStart(state) {
   return {
    ...state,
    loading: { ...state.loading, usersGetSelfAccessGroups: true },
   };
  },
  usersGetSelfAccessGroupsSuccess(
   state,
   action: PayloadAction<AccessGroupsDTO[]>,
  ) {
   return {
    ...state,
    accessGroups: action.payload,
    success: { ...state.success, usersGetSelfAccessGroups: true },
    loading: { ...state.loading, usersGetSelfAccessGroups: null },
   };
  },
  usersGetSelfAccessGroupsFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, usersGetSelfAccessGroups: null },
    loading: { ...state.loading, usersGetSelfAccessGroups: null },
   };
  },
  usersGenerateApiKeyStart(state, action: PayloadAction<{ userId: string }>) {
   return {
    ...state,
    loading: { ...state.loading, usersGenerateApiKey: true },
   };
  },
  usersGenerateApiKeySuccess(state, action: PayloadAction<string>) {
   return {
    ...state,
    apiKey: action.payload,
    success: { ...state.success, usersGenerateApiKey: true },
    loading: { ...state.loading, usersGenerateApiKey: null },
   };
  },
  usersGenerateApiKeyFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, usersGenerateApiKey: null },
    loading: { ...state.loading, usersGenerateApiKey: null },
   };
  },
  usersGetDatabasesStart(state) {
   return {
    ...state,
    loading: { ...state.loading, usersGetDatabases: true },
   };
  },
  usersGetDatabasesSuccess(
   state,
   action: PayloadAction<ConnectionKeysExtendedDTO[]>,
  ) {
   return {
    ...state,
    databases: action.payload,
    success: { ...state.success, usersGetDatabases: true },
    loading: { ...state.loading, usersGetDatabases: null },
    error: { ...state.error, usersGetDatabases: null },
   };
  },
  usersGetDatabasesFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    error: { ...state.error, usersGetDatabases: action.payload },
    success: { ...state.success, usersGetDatabases: null },
    loading: { ...state.loading, usersGetDatabases: null },
   };
  },
  usersAttachDatabaseStart(
   state,
   _action: PayloadAction<SetConnectionKeysDTO[]>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, usersAttachDatabase: true },
    success: { ...state.success, usersAttachDatabase: null },
    error: { ...state.error, usersAttachDatabase: null },
   };
  },
  usersAttachDatabaseSuccess(state) {
   return {
    ...state,
    success: { ...state.success, usersAttachDatabase: true },
    loading: { ...state.loading, usersAttachDatabase: null },
    error: { ...state.error, usersAttachDatabase: null },
   };
  },
  usersAttachDatabaseFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    error: { ...state.error, usersAttachDatabase: action.payload },
    success: { ...state.success, usersAttachDatabase: null },
    loading: { ...state.loading, usersAttachDatabase: null },
   };
  },
  usersAttachDatabaseFinish(state) {
   return {
    ...state,
    success: { ...state.success, usersAttachDatabase: null },
   };
  },
  usersDetachDatabaseStart(
   state,
   _action: PayloadAction<SetConnectionKeysDTO[]>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, usersDetachDatabase: true },
    success: { ...state.success, usersDetachDatabase: null },
    error: { ...state.error, usersDetachDatabase: null },
   };
  },
  usersDetachDatabaseSuccess(state) {
   return {
    ...state,
    success: { ...state.success, usersDetachDatabase: true },
    loading: { ...state.loading, usersDetachDatabase: null },
    error: { ...state.error, usersDetachDatabase: null },
   };
  },
  usersDetachDatabaseFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    error: { ...state.error, usersDetachDatabase: action.payload },
    success: { ...state.success, usersDetachDatabase: null },
    loading: { ...state.loading, usersDetachDatabase: null },
   };
  },
  usersDetachDatabaseFinish(state) {
   return {
    ...state,
    success: { ...state.success, usersDetachDatabase: null },
   };
  },
 },
});

export const {
 // common
 getTableStart,
 getTableSuccess,
 getTableFailure,
 getTableFinish,

 getItemStart,
 getItemSuccess,
 getItemFailure,

 createItemStart,
 createItemSuccess,
 createItemFailure,
 createItemFinish,

 updateItemStart,
 updateItemSuccess,
 updateItemFailure,
 updateItemFinish,

 deleteItemsStart,
 deleteItemsSuccess,
 deleteItemsFailure,
 deleteItemsFinish,

 deleteItemStart,
 deleteItemSuccess,
 deleteItemFailure,
 deleteItemFinish,

 getTableStructureStart,
 getTableStructureSuccess,
 getTableStructureFailure,

 getItemDocumentsStart,
 getItemDocumentsSuccess,
 getItemDocumentsFailure,
 getItemDocumentsFinish,

 uploadItemDocumentsStart,
 uploadItemDocumentsSuccess,
 uploadItemDocumentsFailure,
 uploadItemDocumentsFinish,

 deleteItemDocumentStart,
 deleteItemDocumentSuccess,
 deleteItemDocumentFailure,
 deleteItemDocumentFinish,

 getTableExcelStart,
 getTableExcelSuccess,
 getTableExcelFailure,
 getTableExcelFinish,
 // unique
 usersGetSelfStart,
 usersGetSelfSuccess,
 usersGetSelfFailure,
 usersCreateUserStart,
 usersCreateUserSuccess,
 usersCreateUserFailure,
 usersUpdateSelfStart,
 usersUpdateSelfSuccess,
 usersUpdateSelfFailure,
 usersUpdateSelfFinish,
 usersGetUserGroupsStart,
 usersGetUserGroupsSuccess,
 usersGetUserGroupsFailure,
 usersGetUserGroupStart,
 usersGetUserGroupSuccess,
 usersGetUserGroupFailure,
 usersCreateUserGroupStart,
 usersCreateUserGroupSuccess,
 usersCreateUserGroupFailure,
 usersUpdateUserGroupStart,
 usersUpdateUserGroupSuccess,
 usersUpdateUserGroupFailure,
 usersGetStructureStart,
 usersGetStructureSuccess,
 usersGetStructureFailure,
 usersGetUserAccessGroupsStart,
 usersGetUserAccessGroupsSuccess,
 usersGetUserAccessGroupsFailure,
 usersGetSelfAccessGroupStart,
 usersGetSelfAccessGroupSuccess,
 usersGetSelfAccessGroupFailure,
 usersGetSelfAccessGroupsStart,
 usersGetSelfAccessGroupsSuccess,
 usersGetSelfAccessGroupsFailure,
 usersGenerateApiKeyStart,
 usersGenerateApiKeySuccess,
 usersGenerateApiKeyFailure,
 usersGetDatabasesStart,
 usersGetDatabasesSuccess,
 usersGetDatabasesFailure,
 usersAttachDatabaseStart,
 usersAttachDatabaseSuccess,
 usersAttachDatabaseFailure,
 usersAttachDatabaseFinish,
 usersDetachDatabaseStart,
 usersDetachDatabaseSuccess,
 usersDetachDatabaseFailure,
 usersDetachDatabaseFinish,
} = usersSlice.actions;

export default usersSlice;
